import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Construction,
  FileText,
  Lock,
  Settings2,
  ShieldCheck,
  Tag,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { EditDialog, type FieldDef, FormPanel } from "@/components/resource-page";
import { PolicyLevels } from "@/components/policy-levels";
import { useRecordParam } from "@/hooks/use-record-param";
import { PromoCodesSection } from "@/components/promo-codes";
import {
  type ReasonKind,
  COMPLIMENTARY_GROUPS,
  ENDORSEMENT_GROUP,
  ReasonGroup,
  ReasonsSection,
} from "@/components/reasons";
import { RolesSection } from "@/components/role-editor";
import { Card } from "@/components/ui/card";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { Page, PageHeader } from "@/components/ui/page";
import { SkeletonLines } from "@/components/ui/skeleton";
import { errorMessage } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { deleteResource, listResource } from "@/utils/geo";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";
import { useTypedSearch } from "@/hooks/use-typed-search";

/**
 * Configurations: a left rail of sections, each rendered in place. The
 * section and tab live in the URL so a page can be linked to and reloaded.
 */

const SECTIONS = [
  { key: "notes", label: "cfg.notes", icon: FileText },
  { key: "reasons", label: "cfg.reasons", icon: Settings2 },
  { key: "access-policy", label: "cfg.accessPolicy", icon: Lock },
  { key: "promo-codes", label: "cfg.promoCodes", icon: Tag },
  { key: "business-type", label: "cfg.businessType", icon: Briefcase },
  { key: "business-unit", label: "cfg.businessUnit", icon: Building2 },
  { key: "roles", label: "cfg.roles", icon: ShieldCheck },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const NOTE_TABS = [
  { key: "pre-opening", label: "cfg.preOpening" },
  { key: "complimentary", label: "cfg.complimentary" },
  { key: "endorsement", label: "cfg.endorsement" },
] as const;

type NoteTab = (typeof NOTE_TABS)[number]["key"];

const searchSchema = z.object({
  section: z.enum(SECTIONS.map((s) => s.key) as [SectionKey, ...SectionKey[]]).catch("notes"),
  tab: z.enum(NOTE_TABS.map((t) => t.key) as [NoteTab, ...NoteTab[]]).catch("pre-opening"),
  // Which list has a record open, as `<list>.<token>`. Several lists share a
  // tab, so the parameter names the one it belongs to.
  edit: z.string().optional(),
});

function ConfigurationPage() {
  const t = useTranslation();
  const { section, tab } = useTypedSearch(searchSchema);

  return (
    <Page>
      <PageHeader title={t("cfg.title")} description={t("cfg.subtitle")} />
      <div className="grid gap-8 lg:grid-cols-[232px_minmax(0,1fr)] lg:gap-12">
        {/* A settings rail, sticky under the app bar: which section you are in
            has to stay answered while a long list scrolls. */}
        <aside className="lg:sticky lg:top-[calc(var(--app-header-h)+1.5rem)] lg:self-start">
          <nav
            className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-2 lg:mx-0 lg:grid lg:gap-0.5 lg:px-0 lg:pb-0"
            aria-label={t("cfg.title")}
          >
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <Link
                key={key}
                to={`/configuration?section=${key}&tab=pre-opening`}
                aria-current={section === key ? "page" : undefined}
                className={cn(
                  "text-callout flex h-9 shrink-0 items-center gap-2.5 rounded-lg px-2.5 transition-colors duration-[var(--duration-fast)]",
                  section === key
                    ? "bg-accent-soft text-accent font-medium"
                    : "text-fg hover:bg-surface-inset [&_svg]:text-fg-subtle",
                )}
              >
                <Icon className="size-4.5 shrink-0" aria-hidden />
                <span className="truncate">{t(label)}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <section className="min-w-0">
          {section === "notes" ? (
            <Notes tab={tab} />
          ) : section === "roles" ? (
            <RolesSection />
          ) : section === "access-policy" ? (
            <PolicyLevels />
          ) : section === "promo-codes" ? (
            <PromoCodesSection />
          ) : section === "reasons" ? (
            <ReasonsSection />
          ) : section === "business-type" ? (
            <div className="pr-5">
              <Heading title={t("cfg.businessType")} text={t("cfg.businessTypeText")} />
              <BusinessTypeList />
            </div>
          ) : section === "business-unit" ? (
            <div className="pr-5">
              <Heading title={t("cfg.businessUnit")} text={t("cfg.businessUnitText")} />
              <ClubList />
            </div>
          ) : (
            <Soon section={t(SECTIONS.find((s) => s.key === section)?.label ?? "cfg.notes")} />
          )}
        </section>
      </div>
    </Page>
  );
}

function Heading({ title, text }: { title: string; text: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-title-2 text-heading">{title}</h2>
      <p className="text-fg-muted text-callout mt-1">{text}</p>
    </div>
  );
}

function Notes({ tab }: { tab: NoteTab }) {
  const t = useTranslation();
  // Editing one reason group takes over the tab, so the tab strip and the other
  // lists step aside — a form for one list above an "Add" link for another is
  // an invitation to put the row in the wrong place.
  const [editingKind, setEditingKind] = useState<ReasonKind | null>(null);
  const [editingClub, setEditingClub] = useState(false);
  const editing = editingKind !== null || editingClub;

  return (
    <div className="pr-5">
      {editing ? null : <Heading title={t("cfg.notes")} text={t("cfg.notesText")} />}
      <div
        className={cn("hairline-b mb-5 flex gap-1 overflow-x-auto", editing && "hidden")}
        role="tablist"
      >
        {NOTE_TABS.map(({ key, label }) => (
          <Link
            key={key}
            role="tab"
            aria-selected={tab === key}
            to={`/configuration?section=notes&tab=${key}`}
            className={cn(
              "text-callout -mb-px shrink-0 rounded-t-md border-b-2 px-3 pt-2 pb-2.5 font-medium transition-colors duration-[var(--duration-fast)]",
              tab === key
                ? "border-accent text-fg"
                : "text-fg-muted hover:text-fg border-transparent",
            )}
          >
            {t(label)}
          </Link>
        ))}
      </div>
      {tab === "pre-opening" ? (
        <ClubList onEditingChange={setEditingClub} />
      ) : tab === "endorsement" ? (
        <ReasonGroup
          group={ENDORSEMENT_GROUP}
          onEditingChange={(open) => setEditingKind(open ? ENDORSEMENT_GROUP.kind : null)}
        />
      ) : tab === "complimentary" ? (
        <div className="divide-line divide-y">
          <ClubList
            className="py-6 first:pt-0 last:pb-0"
            title={t("cfg.requestedBy")}
            boxed
            hidden={editingKind !== null}
            onEditingChange={setEditingClub}
          />
          {COMPLIMENTARY_GROUPS.map((group) => (
            <ReasonGroup
              key={group.kind}
              group={group}
              boxed
              className="py-6 first:pt-0 last:pb-0"
              hidden={editingClub || (editing && editingKind !== group.kind)}
              onEditingChange={(open) => setEditingKind(open ? group.kind : null)}
            />
          ))}
        </div>
      ) : (
        <Soon section={t(NOTE_TABS.find((entry) => entry.key === tab)?.label ?? "cfg.notes")} />
      )}
    </div>
  );
}

function Soon({ section }: { section: string }) {
  const t = useTranslation();
  return (
    <Card className="mt-2 grid place-items-center px-6 py-16 text-center">
      <span className="bg-surface-inset text-fg-subtle grid size-11 place-items-center rounded-full">
        <Construction className="size-5" aria-hidden />
      </span>
      <p className="text-title-3 text-heading mt-3">{t("common.comingSoon")}</p>
      <p className="text-fg-muted text-callout mt-1.5">
        {t("common.onItsWay").replace("{section}", section)}
      </p>
    </Card>
  );
}

// --- business types -----------------------------------------------------------

interface BusinessType {
  id: number;
  name: string;
}

const BUSINESS_TYPE_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "name", label: t("cfg.businessTypeField"), required: true },
];

/** The business type list as designed: a name, Delete, and a way to add one. */
function BusinessTypeList({ className }: { className?: string }) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  // Ten at a time; the next ten when the list is scrolled to its end.
  const types = useInfiniteQuery({
    queryKey: ["/business-types", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<BusinessType>(
        "/business-types",
        { page: pageParam, size: LIST_PAGE, sort: "name" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const [deleting, setDeleting] = useState<BusinessType | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/business-types"] });

  const remove = useMutation({
    mutationFn: (id: number) => deleteResource("/business-types", id),
    onSuccess: () => {
      toast.success(t("cfg.businessTypeDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("cfg.businessTypeDeleteFailed"))),
  });

  const rows = types.data?.pages.flatMap((page) => page.items) ?? [];
  const {
    editing: adding,
    setEditing: setAdding,
    loading,
  } = useRecordParam<BusinessType>("edit", "business-type", "/business-types");

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same panel, empty, rather than
  // the list sitting there as though the click had missed.
  if (loading) {
    return (
      <div className={className}>
        <FormPanel
          title={t("rp.editSingular").replace("{singular}", t("cfg.businessType"))}
          onClose={() => setAdding(null)}
          actions={null}
        >
          <Loading busy subject={t("cfg.businessType")}>
            <SkeletonLines rows={3} />
          </Loading>
        </FormPanel>
      </div>
    );
  }

  // Adding takes the place of the list, the way the role editor does, rather
  // than opening a sheet over it.
  if (adding !== null) {
    return (
      <div className={className}>
        <EditDialog
          variant="panel"
          singular={t("cfg.businessType")}
          endpoint="/business-types"
          fields={BUSINESS_TYPE_FIELDS(t)}
          initial={adding === "new" ? {} : { name: adding.name }}
          id={adding === "new" ? null : adding.id}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            void invalidate();
          }}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <ListSection
        title={t("cfg.currentBusinessTypes")}
        shown={rows.length}
        total={types.data?.pages[0]?.total}
        refreshKeys={[["/business-types"]]}
        action={
          allow("add__business_type") ? (
            <AddButton onClick={() => setAdding("new")}>{t("cfg.addBusinessType")}</AddButton>
          ) : undefined
        }
        hasMore={types.hasNextPage}
        loadingMore={types.isFetchingNextPage}
        pending={types.isPending}
        onLoadMore={() => void types.fetchNextPage()}
        empty={t("cfg.noBusinessTypes")}
      >
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-fg text-callout">{row.name}</span>
            {allow("delete__business_type") ? (
              <button
                type="button"
                onClick={() => setDeleting(row)}
                className="text-fg-muted text-subhead hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
              >
                {t("common.delete")}
              </button>
            ) : null}
          </li>
        ))}
      </ListSection>

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("cfg.businessType"))}
        message={deleting ? t("common.confirmDelete").replace("{name}", deleting.name) : ""}
      />
    </div>
  );
}

// --- clubs --------------------------------------------------------------------

interface Club {
  id: number;
  club_name: string;
  club_code: string;
  address: string;
  latitude: number;
  longitude: number;
  city_id: number;
}

const LIST_PAGE = 10;

const CLUB_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "club_name", label: t("cfg.clubName"), required: true },
  { name: "club_code", label: t("cfg.clubCode"), required: true },
  {
    name: "city_id",
    label: t("cfg.city"),
    type: "fk",
    required: true,
    endpoint: "/cities/compact",
  },
  // A branch address runs to a line or two; a single-line box hides all but
  // the start of it, which is the part you already know.
  { name: "address", label: t("cfg.address"), required: true, type: "textarea" as const },
  {
    name: "latitude",
    label: t("cfg.latitude"),
    required: true,
    type: "number",
    step: "any",
    min: -90,
    max: 90,
  },
  {
    name: "longitude",
    label: t("cfg.longitude"),
    required: true,
    type: "number",
    step: "any",
    min: -180,
    max: 180,
  },
];

const toForm = (club: Club): Record<string, string> => ({
  club_name: club.club_name,
  club_code: club.club_code,
  city_id: String(club.city_id),
  address: club.address ?? "",
  latitude: club.latitude?.toString() ?? "",
  longitude: club.longitude?.toString() ?? "",
});

/** The club list as designed: a name, Edit, Delete, and a way to add one. Shared by three sections. */
function ClubList({
  className,
  title,
  boxed = false,
  hidden = false,
  onEditingChange,
}: {
  className?: string;
  title?: string;
  boxed?: boolean;
  /** A sibling is being edited, so this one stands aside. */
  hidden?: boolean;
  /** Tells the parent to stand the other lists down while this one edits. */
  onEditingChange?: (editing: boolean) => void;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  // Ten at a time; the next ten when the list is scrolled to its end.
  const clubs = useInfiniteQuery({
    queryKey: ["/clubs", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<Club>(
        "/clubs",
        { page: pageParam, size: LIST_PAGE, sort: "club_name" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const [deleting, setDeleting] = useState<Club | null>(null);
  const rows = clubs.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<Club>("edit", "club", "/clubs");
  // Editing takes over the tab; the reason groups beside it would otherwise sit
  // under the form still offering to add rows of their own. Reading the record
  // counts as editing, or they sit beside the skeleton instead.
  const editingNow = editing !== null || loading;
  const report = useRef(onEditingChange);
  report.current = onEditingChange;
  useEffect(() => {
    report.current?.(editingNow);
  }, [editingNow]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/clubs"] });

  const remove = useMutation({
    mutationFn: (id: number) => deleteResource("/clubs", id),
    onSuccess: () => {
      toast.success(t("cfg.clubDeleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("cfg.clubDeleteFailed"))),
  });

  if (hidden) return null;

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same panel, empty, rather than
  // the list sitting there as though the click had missed.
  if (loading) {
    return (
      <div className={className}>
        <FormPanel
          title={t("rp.editSingular").replace("{singular}", t("cfg.club"))}
          onClose={() => setEditing(null)}
          actions={null}
        >
          <Loading busy subject={t("cfg.club")}>
            <SkeletonLines rows={3} />
          </Loading>
        </FormPanel>
      </div>
    );
  }

  if (editing !== null) {
    return (
      <div className={className}>
        <EditDialog
          variant="panel"
          singular={t("cfg.club")}
          endpoint="/clubs"
          fields={CLUB_FIELDS(t)}
          initial={editing === "new" ? {} : toForm(editing)}
          id={editing === "new" ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <ListSection
        title={title ?? t("cfg.club")}
        shown={rows.length}
        total={clubs.data?.pages[0]?.total}
        refreshKeys={[["/clubs"]]}
        action={
          allow("add__club") ? (
            <AddButton onClick={() => setEditing("new")}>{t("cfg.addClub")}</AddButton>
          ) : undefined
        }
        hasMore={clubs.hasNextPage}
        loadingMore={clubs.isFetchingNextPage}
        pending={clubs.isPending}
        onLoadMore={() => void clubs.fetchNextPage()}
        empty={t("cfg.noClubs")}
        listClassName={cn(boxed && "border-line [scrollbar-width:thin] rounded-lg border px-4")}
      >
        {rows.map((club) => (
          <li key={club.id} className="flex items-center justify-between gap-4 py-3">
            <span className="min-w-0">
              <span className="flex items-baseline gap-2">
                <span className="text-fg text-callout truncate font-medium uppercase">
                  {club.club_name}
                </span>
                {/* Two branches can share a name; the code is what tells them
                    apart, so it travels with the name rather than only living
                    in the edit dialog. */}
                <span className="text-fg-muted text-footnote shrink-0 tabular-nums">
                  {club.club_code}
                </span>
              </span>
              {club.address ? (
                <span className="text-fg-muted text-subhead mt-0.5 block truncate">
                  {club.address}
                </span>
              ) : null}
            </span>
            <span className="text-subhead flex items-center gap-1">
              {allow("change__club") ? (
                <button
                  type="button"
                  onClick={() => setEditing(club)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {allow("delete__club") ? (
                <button
                  type="button"
                  onClick={() => setDeleting(club)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ListSection>

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("cfg.club"))}
        message={
          deleting
            ? t("common.confirmDelete").replace(
                "{name}",
                `${deleting.club_name} (${deleting.club_code})`,
              )
            : ""
        }
      />
    </div>
  );
}

export default ConfigurationPage;
